/**
 * @file The single cell-coercion axis for extraction — number AND date — shared by
 * the cloud_run write path (the `coerceNumber`/`coerceDate` cleaning rules in
 * `applyColumnRules`, and the `coerceCellValue` gate in the authoritative writers
 * `entityRecordsBulkCreate` / `buildMappedCellsForTableRow` / `applyToDocument`)
 * AND the React Mapping Review preview (`applyCellCleaningRules`), so what the
 * user reviews is exactly what lands. `coerceCellNumber` resolves a raw cell to a
 * finite number or `null`; a value that cannot be resolved becomes `null` (a
 * fixable blank beats a silent 1000× error), and so does an empty cell (never a
 * fabricated `0`) and a non-number in a typed numeric column (never silent text).
 * `coerceCellDate` is the structural parallel on the date axis, and
 * `coerceCellValue` is the field-type dispatcher over both. Raw survives on
 * `meta.importRow` when a coercion returns null.
 *
 * A `NumberLocale` may be supplied (by the agent or the user at propose time,
 * persisted on the staged `ProcessDocument`). With a locale the separators are
 * resolved DETERMINISTICALLY per that convention; without one they are resolved
 * STRUCTURALLY (best guess).
 *
 * Common normalization (both modes): currency glyphs `$€£¥₹` plus anchored alpha /
 * multi-char currency tokens (CHF/kr/US$/R$/Rs/USD/EUR/GBP) stripped; a Swiss
 * apostrophe `'`/`’`, NBSP / narrow-NBSP / thin space all normalized to a plain
 * grouping space; a Unicode minus
 * (U+2212) or dash (U+2010–U+2015) normalized to `-`; a leading `-`/`+` OR
 * accounting parentheses set the sign (parens + inner minus is negative, not a
 * double negative — `(-5)`→-5); a trailing `%` stripped; an embedded newline /
 * tab / vertical whitespace → `null` (two stacked cells must never merge).
 *
 * Structural (no locale) truth table:
 *   - both `,` and `.` → the LAST-occurring separator is the decimal, the other
 *     is thousands and must group validly (`1,234.56`→1234.56, `1.234,56`→1234.56)
 *   - a single `,` OR single `.`, 1–2 trailing digits → decimal (`1,5`→1.5,
 *     `1.5`→1.5)
 *   - a single `,` OR single `.`, exactly 3 trailing digits AND a 1–3 digit
 *     non-zero-leading integer part → `null` (genuinely dual: `1,234`/`1.234`
 *     are thousands-1234 or decimal-1.234 with equal claim). A `0`- or 4+-digit
 *     integer part cannot be thousands grouping, so `0.375`/`1234.567` stay
 *     decimals. The comma and dot branches are symmetric.
 *   - grouped `.`/`,` (`1.234.567`, `1,234,567`) → thousands
 *   - space/NBSP grouped, 1–3 lead then all-3 groups (`1 234 567,89`→1234567.89);
 *     malformed spacing (`12 34`, `1 2`) → `null`
 *   - empty / sign-only / no digits → `null`
 *   - an integer beyond 2^53 (unsafe double precision) → `null`; the raw
 *     survives on `meta.importRow` (map long identifiers to a text column)
 *
 * Locale truth table: the decimal glyph is fixed by the locale (`dotDecimal`→`.`,
 * `commaDecimal`→`,`), the other glyph (and space) is thousands. The decimal may
 * occur at most once, right of all grouping; a value that is not well-formed in
 * the given locale → `null` (`1.234,56` under `dotDecimal` → null). `indianComma`
 * is the third locale: `.` decimal, `,` grouping, but NON-uniform lakh/crore groups
 * (`1,23,456.78`) validated by `isIndianGrouped`, not the uniform-3 rule.
 *
 * STAGE-B AXES (this file is the whole coercion surface, preview == writer):
 * `coerceCellDate` gains `yearFirst` (explicit YMD order), `centuryPivot` (retargets
 * the two-digit-year window) and `dateZone` (interprets a zone-LESS non-ISO
 * date-time's wall clock in a given offset). `coerceCellRange` splits ONE cell on an
 * unambiguous separator (en/em dash, ` to `, spaced ` - `) into a `{start,end}` pair.
 * `coerceCellValue` dispatches range → date → percentage → numeric → yesNo → select,
 * canonicalizing booleans and enum labels to stable option values. `detectCurrency`
 * surfaces the currency symbol/code structurally.
 *
 * FIXED POINT PRESERVED under the new knobs: `centuryPivot`/`dateZone` touch ONLY the
 * non-ISO parse. `toISOString()` still emits a 4-digit-year `…Z` instant, which the
 * ISO branch (which never expands a year and never applies `dateZone`) re-coerces to
 * the identical instant regardless of either knob.
 */

import { NUMERIC_FIELD_DATA_TYPES, DATE_FIELD_DATA_TYPES, RANGE_FIELD_DATA_TYPES } from './System.FieldTypes.types';
import { FieldOption } from './System.FieldOption.types';

/**
 * Numeric-format convention of a source document — which glyph is the decimal
 * separator. `dotDecimal` = `1,234.56` (en-US/UK); `commaDecimal` = `1.234,56`
 * / `1 234,56` (de/es/fr — space grouping is validated structurally under both);
 * `indianComma` = `1,23,456.78` (Indian lakh/crore — comma grouping into NON-uniform
 * groups: a rightmost group of 3, then 2-digit groups, dot decimal). Supplied by the
 * agent or the user at propose time, persisted on the staged `ProcessDocument`, and
 * consumed by every numeric coercion of that staging.
 */
export type NumberLocale = 'dotDecimal' | 'commaDecimal' | 'indianComma';

/** Bare currency glyphs, stripped anywhere in the value. */
const CURRENCY_RE = /[$€£¥₹]/g;
/** Bounded alphabetic / multi-char currency tokens, stripped ONLY when anchored at the
 *  start or end (optional adjacent space) AND not glued to a letter — so a token inside
 *  a word (`entrepreneur`→`eur`, `Krishna`→`Kr`, `3 hrs`→`rs`) is never matched. */
const CURRENCY_TOKEN_RE = /^(?:US\$|R\$|Rs\.?|CHF|USD|EUR|GBP|kr)(?![A-Za-z])\s?|\s?(?<![A-Za-z])(?:US\$|R\$|Rs\.?|CHF|USD|EUR|GBP|kr)$/gi;
/** Straight `'` (U+0027) / curly `’` (U+2019) apostrophe — the Swiss grouping glyph. */
const APOSTROPHE_RE = /['’]/g;
/** NBSP / narrow-NBSP / thin space — grouping spaces locales and OCR emit. */
const GROUPING_SPACE_RE = /[\u00A0\u202F\u2009]/g;
/** Unicode minus + hyphen/dash family OCR emits as a negative sign. */
const MINUS_RE = /[\u2212\u2010-\u2015]/g;
/** Interior newline / tab / vertical whitespace: two stacked values never merge. */
const STACKED_WS_RE = /[\t\n\r\v\f\u0085\u2028\u2029]/;

/** A thousands-grouped integer is a 1–3 digit lead group then all-3-digit groups. */
function isThousandsGrouped(intPart: string, sep: ',' | '.' | ' '): boolean {
    const groups = intPart.split(sep);
    return groups.length > 1
        && /^\d{1,3}$/.test(groups[0])
        && groups.slice(1).every((g) => /^\d{3}$/.test(g));
}

/**
 * Indian lakh/crore grouping: the RIGHTMOST group is exactly 3 digits, every inner
 * group exactly 2, the leftmost 1–2 (`1,23,456` / `12,34,567` / `1,23,45,678`).
 * Rejects a uniform western `1,234,567` (its non-last groups are 3 digits).
 */
function isIndianGrouped(intPart: string): boolean {
    const groups = intPart.split(',');
    if (groups.length < 2) return false;
    if (!/^\d{3}$/.test(groups[groups.length - 1])) return false;
    if (!/^\d{1,2}$/.test(groups[0])) return false;
    return groups.slice(1, -1).every((g) => /^\d{2}$/.test(g));
}

/**
 * Integer part → bare digits. Exactly one of `allowed` may group it (validated);
 * a token mixing two grouping glyphs is malformed and returns `null`.
 */
function ungroupInt(intPart: string, allowed: ReadonlyArray<',' | '.' | ' '>): string | null {
    const used = allowed.filter((sep) => intPart.includes(sep));
    if (used.length === 0) return /^\d*$/.test(intPart) ? intPart : null;
    if (used.length > 1) return null;
    return isThousandsGrouped(intPart, used[0]) ? intPart.split(used[0]).join('') : null;
}

/**
 * Indian lakh/crore grammar: `.` decimal (at most once, right of grouping), `,`
 * grouping validated by `isIndianGrouped` (space is NOT an Indian grouping glyph);
 * a value not well-formed under it → `null` (fail-loud).
 */
function normalizeIndian(s: string): string | null {
    const idx = s.indexOf('.');
    if (idx !== -1 && idx !== s.lastIndexOf('.')) return null;
    const intPart = idx === -1 ? s : s.slice(0, idx);
    const decPart = idx === -1 ? '' : s.slice(idx + 1);
    if (idx !== -1 && !/^\d+$/.test(decPart)) return null;
    const intDigits = intPart.includes(',')
        ? (isIndianGrouped(intPart) ? intPart.split(',').join('') : null)
        : (/^\d*$/.test(intPart) ? intPart : null);
    if (intDigits === null) return null;
    return decPart.length > 0 ? `${intDigits}.${decPart}` : intDigits;
}

/** Deterministic per-locale grammar: one decimal occurrence max, right of all validated grouping. */
function normalizeWithLocale(s: string, locale: NumberLocale): string | null {
    if (locale === 'indianComma') return normalizeIndian(s);
    const decimalSep = locale === 'dotDecimal' ? '.' : ',';
    const groupSep: ',' | '.' = locale === 'dotDecimal' ? ',' : '.';
    const idx = s.indexOf(decimalSep);
    if (idx !== -1 && idx !== s.lastIndexOf(decimalSep)) return null;
    const intPart = idx === -1 ? s : s.slice(0, idx);
    const decPart = idx === -1 ? '' : s.slice(idx + 1);
    if (idx !== -1 && !/^\d+$/.test(decPart)) return null;
    const intDigits = ungroupInt(intPart, [groupSep, ' ']);
    if (intDigits === null) return null;
    return decPart.length > 0 ? `${intDigits}.${decPart}` : intDigits;
}

/**
 * Structural best guess (no locale): the documented conventions above. A
 * malformed multi-separator token (a date `31.12.2023`, a phone `555.123.4567`,
 * an OCR-dropped digit `1.234.56`) is NOT silently stripped into a wrong number;
 * it becomes `null` so the raw survives on `meta.importRow.raw`.
 */
function normalizeStructural(s: string): string | null {
    if (s.includes(' ')) {
        const hasComma = s.includes(',');
        const hasDot = s.includes('.');
        if (hasComma && hasDot) return null;
        if (!hasComma && !hasDot) return ungroupInt(s, [' ']);
        const sep = hasComma ? ',' : '.';
        const idx = s.indexOf(sep);
        if (idx !== s.lastIndexOf(sep)) return null;
        const decPart = s.slice(idx + 1);
        if (!/^\d+$/.test(decPart)) return null;
        const intDigits = ungroupInt(s.slice(0, idx), [' ']);
        return intDigits === null ? null : `${intDigits}.${decPart}`;
    }
    const hasComma = s.includes(',');
    const hasDot = s.includes('.');
    if (hasComma && hasDot) {
        const decimalSep: ',' | '.' = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
        const groupSep: ',' | '.' = decimalSep === ',' ? '.' : ',';
        const idx = s.lastIndexOf(decimalSep);
        if (idx !== s.indexOf(decimalSep)) return null;
        const decPart = s.slice(idx + 1);
        if (!/^\d+$/.test(decPart)) return null;
        const intDigits = ungroupInt(s.slice(0, idx), [groupSep]);
        return intDigits === null ? null : `${intDigits}.${decPart}`;
    }
    if (hasComma) {
        const parts = s.split(',');
        if (parts.length > 2) return ungroupInt(s, [',']);
        if (parts[1].length === 3 && /^[1-9]\d{0,2}$/.test(parts[0])) return null;
        return `${parts[0]}.${parts[1]}`;
    }
    if (hasDot) {
        const parts = s.split('.');
        if (parts.length > 2) return ungroupInt(s, ['.']);
        if (parts[1].length === 3 && /^[1-9]\d{0,2}$/.test(parts[0])) return null;
        return `${parts[0]}.${parts[1]}`;
    }
    return s;
}

export function coerceCellNumber(raw: string | number | null, locale?: NumberLocale): number | null {
    if (raw == null) return null;
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;

    const trimmed = raw.trim();
    if (trimmed === '' || STACKED_WS_RE.test(trimmed)) return null;
    const cleaned = trimmed
        .replace(GROUPING_SPACE_RE, ' ')
        .replace(APOSTROPHE_RE, ' ')
        .replace(MINUS_RE, '-')
        .replace(CURRENCY_TOKEN_RE, '')
        .replace(CURRENCY_RE, '')
        .trim();

    const parenNegative = cleaned.startsWith('(') && cleaned.endsWith(')');
    const unwrapped = (parenNegative ? cleaned.slice(1, -1) : cleaned).trim();
    const unsuffixed = (unwrapped.endsWith('%') ? unwrapped.slice(0, -1) : unwrapped).trim();
    const minusNegative = unsuffixed.startsWith('-');
    const signless = (minusNegative || unsuffixed.startsWith('+') ? unsuffixed.slice(1) : unsuffixed).trim();
    const negative = parenNegative || minusNegative;

    if (!/^[\d., ]+$/.test(signless) || !/\d/.test(signless)) return null;
    const normalized = locale ? normalizeWithLocale(signless, locale) : normalizeStructural(signless);
    if (normalized === null || !/^\d*\.?\d+$/.test(normalized)) return null;

    const num = Number(normalized);
    if (!Number.isFinite(num)) return null;
    if (!normalized.includes('.') && !Number.isSafeInteger(num)) return null;
    return negative ? -num : num;
}

/**
 * Date-format convention of a source document — the field order of an all-numeric
 * date. `dayFirst` = `DD/MM/YYYY` (most of the world); `monthFirst` = `MM/DD/YYYY`
 * (US); `yearFirst` = `YYYY-MM-DD` / YMD order as an EXPLICIT selectable order (a
 * leading 4-digit year is already unambiguous and needs no convention, but a
 * year-first order with a short leading field is selectable here). A token where one
 * field is >12 is self-disambiguating. Supplied by the agent, or resolved at propose
 * time from whole-column evidence (`inferDateLocale`), persisted on the staged
 * `ProcessDocument`, and threaded to every date coercion of that staging. Two-digit
 * years resolve through `CoercionLocales.centuryPivot` when set, else the fixed pivot
 * (00–69 → 20xx, 70–99 → 19xx).
 */
export type DateLocale = 'dayFirst' | 'monthFirst' | 'yearFirst';

/** The scalar date family this coerces; drives the on-disk anchoring below. */
export type DateFieldType = 'date' | 'dateTime' | 'time';

const MONTH_NAMES: Record<string, number> = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8,
    sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
    january: 1, february: 2, march: 3, april: 4, june: 6, july: 7,
    august: 8, september: 9, october: 10, november: 11, december: 12,
};

/** ISO-8601: date-only, or date+time with optional fraction and `Z`/±offset. */
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?)?$/;
/** A trailing time chunk: `HH:MM(:SS)?` with optional am/pm, or a bare `N am/pm`. */
const TRAILING_TIME_RE = /(\d{1,2}:\d{2}(?::\d{2})?(?:\s*[ap]m)?|\d{1,2}\s*[ap]m)\s*$/i;
const COLON_TIME_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?$/i;
const HOUR_AMPM_RE = /^(\d{1,2})\s*(am|pm)$/i;

interface DateParts { y: number; m: number; d: number; }
interface TimeParts { h: number; min: number; s: number; offsetMin: number; }

/**
 * Two-digit year → full year; 3–4 digit years pass through unchanged. With a
 * `centuryPivot P`, a two-digit `n` resolves into the 100-year window `[P, P+99]`
 * (`y = floor(P/100)*100 + n; if (y < P) y += 100`). Without a pivot the fixed
 * default applies (00–69 → 20xx, 70–99 → 19xx, i.e. window pivot 1970) — a
 * DELIBERATE, bounded exception to fail-loud: a fixed pivot is the universal
 * convention (Excel, `strptime %y`, SQL) and two-digit years are the single most
 * common legitimate date format, so failing loud would blank more real data than it
 * protects. The pivot itself is the user-overridable, raw-retained knob.
 */
function expandYear(raw: string, centuryPivot?: number): number {
    const n = Number(raw);
    if (raw.length > 2) return n;
    if (centuryPivot === undefined) return n <= 69 ? 2000 + n : 1900 + n;
    const y = Math.floor(centuryPivot / 100) * 100 + n;
    return y < centuryPivot ? y + 100 : y;
}

/** Calendar round-trip: rejects Feb-30, month 13, etc. by re-reading the built date. */
function validCalendar(y: number, m: number, d: number): boolean {
    const probe = new Date(Date.UTC(y, m - 1, d));
    return probe.getUTCFullYear() === y && probe.getUTCMonth() === m - 1 && probe.getUTCDate() === d;
}

/** `Z`/absent → 0 (a zone-less source is interpreted as UTC); `±HH:MM` → minutes. */
function offsetToMinutes(offset: string | undefined): number {
    if (!offset || offset === 'Z') return 0;
    const sign = offset[0] === '-' ? -1 : 1;
    const digits = offset.slice(1).replace(':', '');
    return sign * (Number(digits.slice(0, 2)) * 60 + Number(digits.slice(2)));
}

/** A parseable UTC offset for the `dateZone` knob: `Z` or `±HH:MM`/`±HHMM`. */
const OFFSET_RE = /^(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Build the canonical `Date`. `dateZone` (non-ISO date-time path ONLY) reinterprets a
 * zone-LESS wall clock: a parseable offset yields that instant, `'naive'`/unset keeps
 * UTC. The ISO path never passes it (fixed point); `date`/`time` types ignore it.
 */
function buildDate(dateType: DateFieldType, date: DateParts | null, time: TimeParts | null, dateZone?: string): Date | null {
    if (dateType === 'time') {
        if (!time) return null;
        const minutes = (((time.h * 60 + time.min - time.offsetMin) % 1440) + 1440) % 1440;
        return new Date(Date.UTC(1970, 0, 1, 0, minutes, time.s));
    }
    if (!date || !validCalendar(date.y, date.m, date.d)) return null;
    if (dateType === 'date') return new Date(Date.UTC(date.y, date.m - 1, date.d, 12, 0, 0));
    const t = time ?? { h: 0, min: 0, s: 0, offsetMin: 0 };
    const offsetMin = dateZone && dateZone !== 'naive' && OFFSET_RE.test(dateZone) ? offsetToMinutes(dateZone) : t.offsetMin;
    return new Date(Date.UTC(date.y, date.m - 1, date.d, t.h, t.min, t.s) - offsetMin * 60000);
}

/** Re-anchor an already-typed `Date` (the fast path) to this type's convention.
 *  `dateTime` truncates sub-second precision so it matches the string path (which
 *  never emits milliseconds), keeping `coerceCellDate` a fixed point on its ISO. */
function reanchor(d: Date, dateType: DateFieldType): Date {
    if (dateType === 'date') return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 12, 0, 0));
    if (dateType === 'time') return new Date(Date.UTC(1970, 0, 1, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()));
    return new Date(Math.floor(d.getTime() / 1000) * 1000);
}

function parseTime(part: string): TimeParts | null {
    const colon = COLON_TIME_RE.exec(part);
    if (colon) {
        const ampm = colon[4]?.toLowerCase();
        const h = ampm === 'pm' && Number(colon[1]) < 12 ? Number(colon[1]) + 12
            : ampm === 'am' && Number(colon[1]) === 12 ? 0 : Number(colon[1]);
        const min = Number(colon[2]);
        const s = colon[3] ? Number(colon[3]) : 0;
        return h > 23 || min > 59 || s > 59 ? null : { h, min, s, offsetMin: 0 };
    }
    const hm = HOUR_AMPM_RE.exec(part);
    if (hm) {
        const ampm = hm[2].toLowerCase();
        const h = ampm === 'pm' && Number(hm[1]) < 12 ? Number(hm[1]) + 12
            : ampm === 'am' && Number(hm[1]) === 12 ? 0 : Number(hm[1]);
        return h > 23 ? null : { h, min: 0, s: 0, offsetMin: 0 };
    }
    return null;
}

/** Split a trailing time chunk off the date part; a pure-time string yields `datePart: ''`. */
function splitDateTime(s: string): { datePart: string; timePart: string | null } {
    const m = TRAILING_TIME_RE.exec(s);
    if (!m || m.index === undefined) return { datePart: s, timePart: null };
    return { datePart: s.slice(0, m.index).trim(), timePart: m[1].trim() };
}

/** All-numeric d/m/y. Leading 4-digit → YMD (unambiguous); an explicit `yearFirst` then
 *  reads the FIRST field as the year (positional YMD, `validCalendar` is the backstop);
 *  otherwise a trailing year with the day/month pair disambiguated by value (>12), then
 *  by `dateLocale` (`dayFirst`/`monthFirst`), else `null`. `yearFirst` MUST precede the
 *  value checks: those assume a trailing year (`expandYear(groups[2])`), which a
 *  year-first token contradicts. */
function parseNumericDate(groups: string[], dateLocale?: DateLocale, centuryPivot?: number): DateParts | null {
    if (groups.length !== 3 || !groups.every((g) => /^\d{1,4}$/.test(g))) return null;
    if (groups[0].length === 4) return { y: Number(groups[0]), m: Number(groups[1]), d: Number(groups[2]) };
    if (dateLocale === 'yearFirst') return { y: expandYear(groups[0], centuryPivot), m: Number(groups[1]), d: Number(groups[2]) };
    const y = expandYear(groups[2], centuryPivot);
    const a = Number(groups[0]);
    const b = Number(groups[1]);
    if (a > 12 && b <= 12) return { y, m: b, d: a };
    if (b > 12 && a <= 12) return { y, m: a, d: b };
    if (a > 12 && b > 12) return null;
    if (dateLocale === 'dayFirst') return { y, m: b, d: a };
    if (dateLocale === 'monthFirst') return { y, m: a, d: b };
    return null;
}

/** A date naming its month (`Jan 5, 2024`, `5 January 2024`); locale-independent. */
function parseMonthNameDate(tokens: string[], centuryPivot?: number): DateParts | null {
    const monthIdx = tokens.findIndex((t) => MONTH_NAMES[t.toLowerCase()] !== undefined);
    if (monthIdx === -1) return null;
    const m = MONTH_NAMES[tokens[monthIdx].toLowerCase()];
    const nums = tokens
        .filter((_, i) => i !== monthIdx)
        .map((t) => t.replace(/(st|nd|rd|th)$/i, ''))
        .filter((t) => /^\d{1,4}$/.test(t));
    if (nums.length !== 2) return null;
    const yearTok = nums.find((t) => t.length >= 3) ?? nums.find((t) => Number(t) > 31);
    const dayTok = nums.find((t) => t !== yearTok);
    if (yearTok === undefined || dayTok === undefined) return null;
    return { y: expandYear(yearTok, centuryPivot), m, d: Number(dayTok) };
}

/**
 * The single date-coercion, mirroring `coerceCellNumber`: resolves a raw cell to a
 * canonical `Date` or `null`, shared by the `coerceDate` cleaning rule (which
 * serializes the result to a canonical ISO string for the review preview) and the
 * write-time `coerceCellValue` gate (which stores the `Date` — never a `Timestamp`
 * built here, which would serialize as a plain map under the cloud_run admin SDK;
 * a `Date` serializes to a native Timestamp under both SDKs and passes
 * `validateDate`). Raw survives on `meta.importRow` when this returns `null`.
 *
 * On-disk anchoring by `dateType`: `date` → 12:00:00 UTC of the calendar day (noon,
 * so a local-zone `dayjs` viewer never renders the previous day); `dateTime` → the
 * exact instant (a zone-less source is interpreted as UTC); `time` → 1970-01-01 UTC.
 *
 * FIXED POINT INVARIANT: ISO-8601 is detected structurally BEFORE the
 * `dateLocale`-dependent d/m/y grammar, so `coerceCellDate`'s own `toISOString()`
 * output re-coerces to the identical instant for every `(dateType, dateLocale)`.
 * This is what lets the preview carry the canonical ISO string and the writer
 * re-coerce it locale-independently. Per-cell ambiguity is fail-loud: a d/m/y token
 * with both fields ≤12 and no `dateLocale` → `null` (raw retained for review); the
 * whole-column order is resolved upstream by `inferDateLocale` at propose time.
 *
 * TWO DELIBERATE EXCEPTIONS to fail-loud, both documented conventions rather than
 * silent guesses: (1) the two-digit-year pivot (see `expandYear`), retargetable via
 * `centuryPivot`; (2) a zone-less `dateTime` is read as UTC rather than nulled, or in
 * an explicit `dateZone` offset when one is supplied — nearly all real datetime
 * columns are zone-less, so failing loud would blank them wholesale. Both knobs touch
 * ONLY the non-ISO parse, so the ISO fixed point above is independent of them.
 */
export function coerceCellDate(
    raw: string | number | Date | null,
    dateType: DateFieldType,
    dateLocale?: DateLocale,
    centuryPivot?: number,
    dateZone?: string,
): Date | null {
    if (raw == null) return null;
    if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : reanchor(raw, dateType);
    if (typeof raw === 'number') return null;

    const s = raw.trim();
    if (s === '' || STACKED_WS_RE.test(s)) return null;

    const iso = ISO_RE.exec(s);
    if (iso) {
        const date: DateParts = { y: Number(iso[1]), m: Number(iso[2]), d: Number(iso[3]) };
        const time: TimeParts | null = iso[4] !== undefined
            ? { h: Number(iso[4]), min: Number(iso[5]), s: iso[6] ? Number(iso[6]) : 0, offsetMin: offsetToMinutes(iso[7]) }
            : null;
        if (time && (time.h > 23 || time.min > 59 || time.s > 59)) return null;
        return buildDate(dateType, date, time);
    }

    if (dateType === 'time') return buildDate('time', null, parseTime(s) ?? parseTime(splitDateTime(s).timePart ?? ''));

    const { datePart, timePart } = splitDateTime(s);
    const time = timePart ? parseTime(timePart) : null;
    if (timePart && time === null) return null;
    const tokens = datePart.split(/[/\-.,\s]+/).filter(Boolean);
    const date = /[a-z]/i.test(datePart) ? parseMonthNameDate(tokens, centuryPivot) : parseNumericDate(tokens, dateLocale, centuryPivot);
    return buildDate(dateType, date, time, dateZone);
}

/** Unambiguous single-cell range separators. A bare `-` is NOT one (it is part of an
 *  ISO date `2024-01-15`); only an en/em dash, a spaced ` - `, or ` to ` splits. */
const RANGE_DASH_RE = /[–—]/;
const RANGE_WORD_RE = /\s+to\s+/i;
const RANGE_HYPHEN_RE = /\s-\s/;

/** Split one cell into exactly two non-empty halves on the first present unambiguous
 *  separator; anything else (no separator, >2 halves, an empty half) → `null`. */
function splitRange(s: string): [string, string] | null {
    const sep = RANGE_DASH_RE.test(s) ? RANGE_DASH_RE
        : RANGE_WORD_RE.test(s) ? RANGE_WORD_RE
            : RANGE_HYPHEN_RE.test(s) ? RANGE_HYPHEN_RE
                : null;
    if (sep === null) return null;
    const parts = s.split(sep);
    if (parts.length !== 2) return null;
    const start = parts[0].trim();
    const end = parts[1].trim();
    return start === '' || end === '' ? null : [start, end];
}

/**
 * Single-cell range coercion, the `{start,end}` parallel of `coerceCellDate`: splits
 * ONE cell (two-column assembly is the writer's job) on an UNAMBIGUOUS separator into
 * two halves, coerces each with `coerceCellDate` under the mapped scalar type
 * (`timeRange`→`time`, `dateRange`→`date`, `dateTimeRange`→`dateTime`), and fails loud
 * (→ `null`) on: no unambiguous separator (a bare `-` inside an ISO date never splits,
 * so a range needs an en/em dash, ` to `, or a spaced ` - `), not exactly two non-empty
 * halves, either half uncoercible, or an inverted range (`start > end`).
 */
export function coerceCellRange(
    raw: string | number | Date | null,
    rangeType: 'timeRange' | 'dateRange' | 'dateTimeRange',
    dateLocale?: DateLocale,
    centuryPivot?: number,
    dateZone?: string,
): { start: Date; end: Date } | null {
    if (typeof raw !== 'string') return null;
    const s = raw.trim();
    if (s === '' || STACKED_WS_RE.test(s)) return null;
    const scalarType: DateFieldType = rangeType === 'timeRange' ? 'time' : rangeType === 'dateRange' ? 'date' : 'dateTime';
    const halves = splitRange(s);
    if (halves === null) return null;
    const start = coerceCellDate(halves[0], scalarType, dateLocale, centuryPivot, dateZone);
    const end = coerceCellDate(halves[1], scalarType, dateLocale, centuryPivot, dateZone);
    if (start === null || end === null) return null;
    return start.getTime() > end.getTime() ? null : { start, end };
}

/**
 * Whole-column date-order evidence, run once at propose time (NOT per cell): a
 * self-disambiguating token (`31/12/…` → dayFirst; `12/31/…` → monthFirst) is a
 * structural fact about the data, not a heuristic. Contradictory evidence in one
 * column → `undefined` (unresolved; do not majority-vote), which leaves genuinely
 * ambiguous cells to null-and-retain-raw while unambiguous ones still coerce.
 *
 * DELIBERATE, do not "fix" without asking: a 2-digit-leading token like `24/01/15`
 * reads as dayFirst, so a genuine yy/mm/dd column is silently mis-read. Abstaining
 * loudly there would also stop auto-inferring common European dd/mm/yy, which is far
 * more frequent; the mis-read is mitigated by the provenance chip and explicit override.
 */
export function inferDateLocale(values: ReadonlyArray<string | number | null>): DateLocale | undefined {
    let sawDayFirst = false;
    let sawMonthFirst = false;
    for (const v of values) {
        if (typeof v !== 'string') continue;
        const s = v.trim();
        if (s === '' || ISO_RE.test(s) || /[a-z]/i.test(s)) continue;
        const groups = splitDateTime(s).datePart.split(/[/\-.,\s]+/).filter(Boolean);
        if (groups.length !== 3 || groups[0].length === 4 || !groups.every((g) => /^\d{1,4}$/.test(g))) continue;
        const a = Number(groups[0]);
        const b = Number(groups[1]);
        if (a > 12 && a <= 31 && b <= 12) sawDayFirst = true;
        else if (b > 12 && b <= 31 && a <= 12) sawMonthFirst = true;
    }
    if (sawDayFirst && sawMonthFirst) return undefined;
    return sawDayFirst ? 'dayFirst' : sawMonthFirst ? 'monthFirst' : undefined;
}

/** Locales/knobs threaded from the staged doc into the write-time gate. */
export interface CoercionLocales {
    numberLocale?: NumberLocale;
    dateLocale?: DateLocale;
    /** 4-digit pivot year; a two-digit year `yy` resolves into the 100-year window
     *  starting at the pivot (see `expandYear`). Unset → the fixed 1970 pivot. */
    centuryPivot?: number;
    /** Offset (`'+05:30'`/`'Z'`) applied to a zone-LESS non-ISO date-time so its wall
     *  clock is read in that zone; `'naive'` or unset interprets it as UTC (no shift). */
    dateZone?: string;
}

/** Per-field context the dispatcher needs beyond the type id: the enum option set and
 *  the percentage scale hint (`'percent'` = the value is already a percent; `'fraction'`
 *  = a 0–1 fraction to be multiplied by 100). A literal `%` in the cell overrides it. */
export interface FieldCoercionContext {
    options?: FieldOption[];
    percentScale?: 'percent' | 'fraction';
}

const TRUE_TOKENS: ReadonlySet<string> = new Set([
    'yes', 'y', 'true', 't', '1', 'sí', 'si', 'oui', 'ja', 'sim', 'да',
]);
const FALSE_TOKENS: ReadonlySet<string> = new Set([
    'no', 'n', 'false', 'f', '0', 'não', 'nao', 'non', 'nein', 'nee', 'нет',
]);

/** A localized yes/no token → real boolean; an already-boolean passes through; an
 *  unrecognized token → `null` (fail-loud, raw retained). */
function coerceBoolean(raw: string | number | boolean | Date | null): boolean | null {
    if (typeof raw === 'boolean') return raw;
    if (raw == null || raw instanceof Date) return null;
    const token = String(raw).trim().toLowerCase();
    if (TRUE_TOKENS.has(token)) return true;
    if (FALSE_TOKENS.has(token)) return false;
    return null;
}

/** Canonicalize one enum token to a stable option `value`: exact `value`, then
 *  case-insensitive `label`, then case-insensitive `aliases`; no match → `null`. */
function matchOption(token: string, options: FieldOption[]): string | null {
    const trimmed = token.trim();
    const lower = trimmed.toLowerCase();
    const match = options.find((o) => o.value === trimmed)
        ?? options.find((o) => o.label.toLowerCase() === lower)
        ?? options.find((o) => o.aliases?.some((a) => a.toLowerCase() === lower));
    return match ? match.value : null;
}

/**
 * Enum canonicalization. WITHOUT options the raw passes through unchanged (cannot
 * canonicalize, must not null-out); WITH options a non-matching token fails loud
 * (`null`). `multiSelect` splits on `,`/`;`, maps every token, and rejects the whole
 * cell if ANY token is unmapped; `singleSelect` maps the one token.
 */
function coerceEnum(
    raw: string | number | Date | null,
    kind: 'singleSelect' | 'multiSelect',
    options?: FieldOption[],
): string | number | Date | null {
    if (!options || options.length === 0) return raw;
    if (raw == null || raw instanceof Date) return null;
    const cell = String(raw);
    if (kind === 'singleSelect') return matchOption(cell, options);
    const tokens = cell.split(/[,;]/).map((t) => t.trim()).filter((t) => t !== '');
    if (tokens.length === 0) return null;
    const mapped = tokens.map((t) => matchOption(t, options));
    return mapped.some((m) => m === null) ? null : mapped.join(',');
}

/**
 * Percentage magnitude via `coerceCellNumber` (which strips a trailing `%`). A literal
 * `%` in the cell is a structural "already a percent" fact → no scaling; otherwise a
 * `'fraction'` scale hint multiplies a 0–1 fraction by 100; `'percent'`/unset → no
 * scaling. A `null` magnitude → `null`.
 */
function coercePercentage(
    raw: string | number | Date | null,
    numberLocale?: NumberLocale,
    percentScale?: 'percent' | 'fraction',
): number | null {
    const magnitude = coerceCellNumber(raw instanceof Date ? null : raw, numberLocale);
    if (magnitude === null) return null;
    if (typeof raw === 'string' && raw.includes('%')) return magnitude;
    return percentScale === 'fraction' ? magnitude * 100 : magnitude;
}

/**
 * The single write-time gate the three record/table/document writers call in place of
 * the copy-pasted type branches, and the exact function the React preview runs so
 * preview == writer. First-match dispatch over `fieldDataTypeId`: (1) range family →
 * `coerceCellRange`; (2) scalar date family → `coerceCellDate`; (3) `percentage`
 * (BEFORE the generic numeric set); (4) `currency` / remaining numeric →
 * `coerceCellNumber`; (5) `yesNo` → boolean; (6) `singleSelect`/`multiSelect` → enum
 * canonicalization; (7) else passthrough. Returns `lost` — true when a non-empty cell
 * coerced to `null` — so writers stash the raw on `meta.importRow` without re-deriving
 * "was this coercible".
 */
export function coerceCellValue(
    raw: string | number | Date | null,
    fieldDataTypeId: string | undefined,
    locales: CoercionLocales,
    fieldContext?: FieldCoercionContext,
): { value: string | number | boolean | Date | { start: Date; end: Date } | null; lost: boolean } {
    const id = fieldDataTypeId;
    const nonEmpty = raw != null && String(raw).trim() !== '';

    if (id && RANGE_FIELD_DATA_TYPES.has(id)) {
        const value = coerceCellRange(raw, id as 'timeRange' | 'dateRange' | 'dateTimeRange', locales.dateLocale, locales.centuryPivot, locales.dateZone);
        return { value, lost: value === null && nonEmpty };
    }
    if (id && DATE_FIELD_DATA_TYPES.has(id)) {
        const value = coerceCellDate(raw, id as DateFieldType, locales.dateLocale, locales.centuryPivot, locales.dateZone);
        return { value, lost: value === null && nonEmpty };
    }
    if (id === 'percentage') {
        const value = coercePercentage(raw, locales.numberLocale, fieldContext?.percentScale);
        return { value, lost: value === null && nonEmpty };
    }
    if (id === 'currency' || (id && NUMERIC_FIELD_DATA_TYPES.has(id))) {
        const value = coerceCellNumber(raw instanceof Date ? null : raw, locales.numberLocale);
        return { value, lost: value === null && nonEmpty };
    }
    if (id === 'yesNo') {
        const value = coerceBoolean(raw);
        return { value, lost: value === null && nonEmpty };
    }
    if (id === 'singleSelect' || id === 'multiSelect') {
        const value = coerceEnum(raw, id, fieldContext?.options);
        return { value, lost: value === null && nonEmpty };
    }
    return { value: raw, lost: false };
}

/**
 * Pure structural currency detection (used by propose + writers): the currency
 * symbol/code at the value's start or end via the SAME anchored patterns
 * `coerceCellNumber` strips — a glyph (`$€£¥₹`) or a bounded alpha/multi-char token
 * (CHF/kr/US$/R$/Rs/USD/EUR/GBP) — else `undefined`. No guessing.
 */
export function detectCurrency(raw: string | number | null): string | undefined {
    if (raw == null || typeof raw === 'number') return undefined;
    const s = raw.trim();
    if (s === '') return undefined;
    const token = s.match(CURRENCY_TOKEN_RE);
    if (token) return token[0].trim();
    const glyph = s.match(CURRENCY_RE);
    return glyph ? glyph[0] : undefined;
}
